import { FC } from 'react';
import {
    TextField as MuiTextField,
    TextFieldProps as MuiTextFieldProps,
    InputAdornment,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

export interface FormTextFieldProps extends Omit<MuiTextFieldProps, 'name'> {
    name: string;
    label: string;
    placeholder?: string;
    helperText?: string;
    showSuccessIcon?: boolean;
    showErrorIcon?: boolean;
    endAdornment?: React.ReactNode;
    startAdornment?: React.ReactNode;
}

export const FormTextField: FC<FormTextFieldProps> = ({
    name,
    label,
    placeholder,
    helperText,
    required = false,
    disabled = false,
    type = 'text',
    multiline = false,
    rows = 4,
    showSuccessIcon = true,
    showErrorIcon = true,
    endAdornment,
    startAdornment,
    ...rest
}) => {
    const {
        control,
        formState: { errors },
    } = useFormContext();

    const error = errors[name];
    const hasError = !!error;
    const errorMessage = error?.message as string;

    // For successful validation (field is touched and no error)
    const isValid = !hasError && control._formState.touchedFields[name];

    return (
        <Controller
            name={name}
            control={control}
            render={({ field }) => (
                <MuiTextField
                    {...field}
                    {...rest}
                    label={label}
                    placeholder={placeholder}
                    required={required}
                    disabled={disabled}
                    type={type}
                    multiline={multiline}
                    rows={multiline ? rows : undefined}
                    fullWidth
                    error={hasError}
                    helperText={hasError ? errorMessage : helperText}
                    variant="outlined"
                    InputProps={{
                        startAdornment: startAdornment ? (
                            <InputAdornment position="start">{startAdornment}</InputAdornment>
                        ) : undefined,
                        endAdornment: (
                            <>
                                {endAdornment && (
                                    <InputAdornment position="end">{endAdornment}</InputAdornment>
                                )}
                                {showErrorIcon && hasError && (
                                    <InputAdornment position="end">
                                        <ErrorIcon color="error" fontSize="small" />
                                    </InputAdornment>
                                )}
                                {showSuccessIcon && isValid && (
                                    <InputAdornment position="end">
                                        <CheckCircleIcon color="success" fontSize="small" />
                                    </InputAdornment>
                                )}
                            </>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '&.Mui-focused fieldset': {
                                borderWidth: 2,
                            },
                            '&.Mui-error fieldset': {
                                borderWidth: 2,
                            },
                        },
                    }}
                />
            )}
        />
    );
};

